import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar as CalendarIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HolidayCalendar = ({ user, setUser }) => {
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/holidays?year=${selectedYear}`, config);
      setHolidays(response.data);
    } catch (error) {
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const groupHolidaysByMonth = () => {
    const grouped = {};
    holidays.forEach(holiday => {
      const date = new Date(holiday.date);
      const month = date.toLocaleString('default', { month: 'long' });
      if (!grouped[month]) grouped[month] = [];
      grouped[month].push(holiday);
    });
    return grouped;
  };

  const groupedHolidays = groupHolidaysByMonth();
  const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const getHolidayBadge = (type) => {
    return type === 'organization'
      ? 'bg-orange-100 text-orange-800 border border-orange-300'
      : 'bg-purple-100 text-purple-800 border border-purple-300';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-orange-50" data-testid="holiday-calendar-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Holiday Calendar</h1>
            <p className="text-gray-600">View organization and restricted holidays</p>
          </div>
          <Select value={String(selectedYear)} onValueChange={(value) => setSelectedYear(Number(value))}>
            <SelectTrigger className="w-32" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(year => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Legend */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500 rounded"></div>
                <span className="text-sm">Organization Holiday (OH)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-purple-500 rounded"></div>
                <span className="text-sm">Restricted Holiday (RH)</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">Loading holidays...</div>
        ) : holidays.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              No holidays defined for {selectedYear}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {monthOrder.map(month => {
              const monthHolidays = groupedHolidays[month];
              if (!monthHolidays || monthHolidays.length === 0) return null;

              return (
                <Card key={month} className="shadow-md hover:shadow-lg transition-shadow" data-testid={`month-card-${month}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5 text-blue-600" />
                      {month} {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {monthHolidays
                        .sort((a, b) => new Date(a.date) - new Date(b.date))
                        .map(holiday => (
                          <div
                            key={holiday.id}
                            className="p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors"
                            data-testid={`holiday-${holiday.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{holiday.name}</p>
                                <p className="text-sm text-gray-600 mt-1">
                                  {new Date(holiday.date).toLocaleDateString('en-US', {
                                    weekday: 'short',
                                    month: 'short',
                                    day: 'numeric'
                                  })}
                                </p>
                              </div>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getHolidayBadge(holiday.holiday_type)}`}>
                                {holiday.holiday_type === 'organization' ? 'OH' : 'RH'}
                              </span>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HolidayCalendar;
